<template>
  <div class="users">
    <form class="users__form" @submit.prevent="onSubmit">
      <base-form-input class="form__input" label="Email, username, or id">
        <input v-model="input" :placeholder="'john@doe.com or johndoe'" />
      </base-form-input>
      <base-button type="submit">Search</base-button>
    </form>

    <router-view />
  </div>
</template>

<script lang="ts">
import { MeetAndGreetTicketPayment, Query, User } from '@/types/api';
import gql from 'graphql-tag';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { getErrorCodes } from '../utils/errors';

function isUser(customer: Record<string, unknown> | null | undefined): customer is User {
  return customer?.__typename === 'User';
}

function isMeetAndGreetTicketPayment(
  customer: Record<string, unknown> | null | undefined
): customer is MeetAndGreetTicketPayment {
  return customer?.__typename === 'MeetAndGreetTicketPayment';
}

@Component({})
export default class SearchView extends Vue {
  input = '';

  public get searchQuery() {
    return (this.$route.query.q as string) || '';
  }

  private get isInputEmail() {
    return this.input.includes('@');
  }

  private get isInputUuid() {
    return this.input.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);
  }

  private get isInputUsername() {
    // Username: not email, not UUID, and contains valid characters
    return !this.isInputEmail && !this.isInputUuid && this.input.trim().length > 0;
  }

  private get variables() {
    if (this.isInputUuid) return { id: this.input };
    if (this.isInputEmail) return { email: this.input };
    if (this.isInputUsername) return { username: this.input };
    return null;
  }

  @Watch('searchQuery', { immediate: true })
  public onSearchQueryChange() {
    this.input = this.searchQuery;
    if (this.input) this.search();
  }

  onSubmit() {
    this.$router.replace({ query: { q: this.input.trim() } }).catch(() => {});
  }

  private async search() {
    if (!this.variables) {
      this.$modal.alert({ title: 'Invalid input', message: 'Please enter a valid email, username, or user id' });
      return;
    }
    const result = await this.$apollo.query<Pick<Query, 'customer'>>({
      query: gql`
        query Customer($id: ID, $email: String, $username: String, $qr: String) {
          customer(id: $id, email: $email, username: $username, qr: $qr) {
            __typename
            id
          }
        }
      `,
      variables: this.variables,
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    });
    if (result.errors) {
      const errorCodes = getErrorCodes(result.errors);
      if (errorCodes.includes('NotFoundError')) {
        this.$modal.alert({ title: 'Not found', message: 'Customer not found' });
        return;
      }
      throw new Error('Unexpected error');
    }
    if (isUser(result.data.customer)) {
      this.$router.replace({ name: 'user', params: { id: result.data.customer.id } });
    }
    if (isMeetAndGreetTicketPayment(result.data.customer)) {
      this.$router.replace({ name: 'meet-and-greet-ticket-payment', params: { id: result.data.customer.id } });
    }
  }
}
</script>

<style lang="scss" scoped>
.users {
}

.users__form {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-end;

  .form__input {
    flex-grow: 1;
    max-width: 400px;
    margin-right: 8px;
  }
}
</style>
